'use client';
import { useState, useCallback, useMemo } from 'react';

type UseStepActions<Step> = {
  goToNextStep: () => void;
  goToPrevStep: () => void;
  canGoToNextStep: boolean;
  canGoToPrevStep: boolean;
  setStep: (step: Step) => void;
  reset: () => void;
};

type UseStepArgs<Step extends string | number> = {
  steps: readonly Step[];
  defaultStep?: Step;
};

export function useStep<Step extends string | number>(args: UseStepArgs<Step>): [Step, UseStepActions<Step>] {
  const { steps, defaultStep } = args;

  const initialStep = defaultStep ?? steps[0];
  const initialIndex = useMemo(() => {
    const idx = steps.indexOf(initialStep);
    return idx >= 0 ? idx : 0;
  }, [steps, initialStep]);

  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  const maxIndex = steps.length - 1;
  const canGoToNextStep = currentIndex < maxIndex;
  const canGoToPrevStep = currentIndex > 0;

  const goToNextStep = useCallback(() => {
    if (currentIndex < maxIndex) {
      setCurrentIndex((prev) => prev + 1);
    }
  }, [currentIndex, maxIndex]);

  const goToPrevStep = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  }, [currentIndex]);

  const setStep = useCallback(
    (step: Step) => {
      const idx = steps.indexOf(step);
      if (idx >= 0) {
        setCurrentIndex(idx);
      }
    },
    [steps],
  );

  const reset = useCallback(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  return [
    steps[currentIndex],
    {
      goToNextStep,
      goToPrevStep,
      canGoToNextStep,
      canGoToPrevStep,
      setStep,
      reset,
    },
  ];
}
